import { Callout } from "components/Form/components/Callout/Callout";
import { requiredValidator } from "components/Form/Form.utils";
import { IconCollection } from "components/Icon/IconCollection";
import RecordLabel from "components/RecordLabel";
import { ResourceConfig } from "config/config";
import {
    DeleteListAction,
    PermanentlyDeleteListAction,
    deleteActionSignature,
    deletePermanentlyActionSignature,
    duplicateActionSignature,
    exportActionSignature,
    moveToLocationActionSignature,
} from "containers/Resources/components/CommonActions/CommonActions";
import { commentsTab, logsTab, logsTabKey, metadataTab } from "containers/Resources/screens/ResourcesSingle/constants";
import { isEnabledColumn } from "containers/Search/components/SearchTabs/columns";
import { getRelativeUrl } from "utils/service";
import { serializeToPrettyYaml } from "utils/valueTransformers";
import resource from "./resource";
import { getLatestExecutions } from "./sharedFields";

const isToolEnabled = (currentValues: { is_enabled?: boolean }) => currentValues?.is_enabled === true;

const enabledToolProps = {
    getDisabled: isToolEnabled,
    fieldTooltip: (currentValues: { is_enabled?: boolean }) => {
        if (isToolEnabled(currentValues)) {
            return "Disable the tool before changing this field.";
        }
    },
};

const isEnabledFieldDisabled = (currentValues: { flow_id?: string }) => !currentValues?.flow_id;

const isEnabledFieldProps = {
    getDisabled: isEnabledFieldDisabled,
    fieldTooltip: (currentValues: { flow_id?: string } = {}) => {
        if (!isEnabledFieldDisabled(currentValues)) {
            return;
        }
        return "Select a flow before enabling this MCP tool.";
    },
};

const isReadOnlyHintDisabled = (currentValues: { is_enabled?: boolean; destructive_hint?: boolean }) =>
    isToolEnabled(currentValues) || currentValues?.destructive_hint === true;

const readOnlyHintFieldProps = {
    getDisabled: isReadOnlyHintDisabled,
    fieldTooltip: (currentValues: { is_enabled?: boolean; destructive_hint?: boolean } = {}) => {
        if (isToolEnabled(currentValues)) return "Disable the tool before changing this field.";
        if (currentValues?.destructive_hint === true)
            return "Read-only hint cannot be enabled while destructive hint is active.";
    },
};

const isDestructiveHintDisabled = (currentValues: { is_enabled?: boolean; read_only_hint?: boolean }) =>
    isToolEnabled(currentValues) || currentValues?.read_only_hint === true;

const destructiveHintFieldProps = {
    getDisabled: isDestructiveHintDisabled,
    fieldTooltip: (currentValues: { is_enabled?: boolean; read_only_hint?: boolean } = {}) => {
        if (isToolEnabled(currentValues)) return "Disable the tool before changing this field.";
        if (currentValues?.read_only_hint === true)
            return "Destructive hint does not apply when read-only hint is enabled.";
    },
};

const buildMcpToolAnnotations = (currentValues: {
    read_only_hint?: boolean;
    destructive_hint?: boolean;
}) => {
    if (currentValues?.read_only_hint === true) {
        return { readOnlyHint: true, destructiveHint: false };
    }
    return {
        readOnlyHint: false,
        destructiveHint: currentValues?.destructive_hint !== false,
    };
};

// Mirror the backend MCP contract (workspace/mcp/user_tools.py):
// - a flow output_schema is advertised as the tool outputSchema (and returned as
//   structuredContent) ONLY when it is an OBJECT schema (type: "object");
// - a missing/empty input_schema or non-object output_schema is logged as a warning
//   server-side. We surface the same signals on the record screen.
const isEmptySchema = (schema: unknown): boolean =>
    schema == null ||
    (typeof schema === "object" && Object.keys(schema as Record<string, unknown>).length === 0);

const isObjectSchema = (schema: unknown): boolean =>
    typeof schema === "object" &&
    schema !== null &&
    (schema as { type?: unknown }).type === "object";

const mcpOutputSchemaFromFlow = (outputSchema: unknown): Record<string, unknown> | undefined =>
    isObjectSchema(outputSchema) ? (outputSchema as Record<string, unknown>) : undefined;

const backingFlowSchemaWarning = (currentValues: {
    flow_id?: string;
    flow_id_flow?: { id?: string; name?: string; input_schema?: unknown; output_schema?: unknown };
}) => {
    const flow = currentValues?.flow_id_flow;
    // Schemas refresh on save, so only evaluate the SAVED backing flow and avoid a
    // stale warning when the flow selection was changed but not yet saved.
    if (!flow || !currentValues?.flow_id || flow.id !== currentValues.flow_id) {
        return null;
    }
    const notices: string[] = [];
    if (isEmptySchema(flow.input_schema)) {
        notices.push(
            "no input_schema is declared — the tool advertises an empty inputSchema and accepts no structured arguments",
        );
    }
    if (!isObjectSchema(flow.output_schema)) {
        notices.push(
            isEmptySchema(flow.output_schema)
                ? "no output_schema is declared — the tool will not advertise an outputSchema or return structuredContent (raw-text passthrough)"
                : "output_schema is not an object schema (type: object) — the tool will not advertise an outputSchema or return structuredContent (raw-text passthrough)",
        );
    }
    if (notices.length === 0) {
        return null;
    }
    return (
        <Callout
            type="warning"
            text={`Flow "${flow.name}": ${notices.join(
                "; ",
            )}. For a first-class MCP tool, declare both an object input_schema and output_schema on the flow.`}
        />
    );
};

const resourceName = "mcp_user_tool";

const config: ResourceConfig = {
    label: "MCP user tool",
    icon: IconCollection.memory,
    resourceName,
    resourceClass: "resource",
    defaultActiveTab: logsTabKey,
    layout: [
        { partial: "pageHeaderForm" },
        { partial: "form" },
        {
            partial: "tabs",
            config: {
                tabs: [logsTab.tab, metadataTab.tab, commentsTab.tab],
                content: [logsTab.content, metadataTab.content, commentsTab.content],
            },
        },
    ],
    graphql: {
        resource: resourceName,
        resourceBody: `
            ${resource.graphql.resourceBody}
            flow_id
            flow_id_flow {
                id
                name
                input_schema
                output_schema
            }
            timeout_sec
            is_enabled
            announced
            read_only_hint
            destructive_hint
        `,
        resourceBodyListing: `
            ${resource.graphql.resourceBody}
            is_enabled
            announced
            flow_id_flow {
                id
                name
            }
            timeout_sec
        `,
        createVariables: `
            ${resource.graphql.createVariables(resourceName)}
            $flow_id: String,
            $timeout_sec: Int,
            $is_enabled: Boolean,
            $announced: Boolean,
            $read_only_hint: Boolean,
            $destructive_hint: Boolean,
        `,
        createQuery: `
            ${resource.graphql.createQuery(resourceName)}
            flow_id: $flow_id,
            timeout_sec: $timeout_sec,
            is_enabled: $is_enabled,
            announced: $announced,
            read_only_hint: $read_only_hint,
            destructive_hint: $destructive_hint,
        `,
        updateVariables: `
            ${resource.graphql.updateVariables(resourceName)}
            $flow_id: String,
            $timeout_sec: Int,
            $is_enabled: Boolean,
            $announced: Boolean,
            $read_only_hint: Boolean,
            $destructive_hint: Boolean,
        `,
        updateQuery: `
            ${resource.graphql.updateQuery(resourceName)}
            flow_id: $flow_id,
            timeout_sec: $timeout_sec,
            is_enabled: $is_enabled,
            announced: $announced,
            read_only_hint: $read_only_hint,
            destructive_hint: $destructive_hint,
        `,
    },
    actions: {
        shown: [],
        hidden: [
            duplicateActionSignature,
            exportActionSignature,
            moveToLocationActionSignature,
            deleteActionSignature,
            deletePermanentlyActionSignature,
        ],
    },
    listActions: {
        shown: [],
        hidden: [
            { key: "delete", action: DeleteListAction },
            { key: "deletePermanently", action: PermanentlyDeleteListAction },
        ],
    },
    additionalColumns: (setNewValue) => [
        isEnabledColumn(setNewValue),
        {
            label: "Flow",
            key: "flow",
            shown: true,
            dto: ({ flow_id_flow }: { flow_id_flow?: { id: string; name: string } }) =>
                flow_id_flow && (
                    <RecordLabel
                        link={getRelativeUrl("flow", flow_id_flow.id)}
                        name={flow_id_flow.name}
                        iconByType="flow"
                    />
                ),
        },
        {
            label: "Timeout (s)",
            key: "timeout_sec",
            shown: false,
            sortable: true,
            dto: (value: { timeout_sec?: number }) => value?.timeout_sec ?? 300,
        },
        {
            label: "Announced",
            key: "announced",
            shown: false,
            sortable: true,
            dto: (value: { announced?: boolean }) => (value?.announced ? "Yes" : "No"),
        },
    ],
    form: [
        {
            name: "flow_id",
            component: "autocomplete",
            fieldProps: {
                label: "Flow",
                options: {
                    resource: "flow",
                },
                docs: {
                    text: "Flow executed when an agent invokes this MCP tool. Input and output schemas come from this flow.",
                    urlPath: "/docs/ai-agents/mcp-server#user-tools",
                },
                fieldNote: backingFlowSchemaWarning,
            },
            fieldAttr: {
                required: true,
                ...enabledToolProps,
            },
            validation: requiredValidator,
        },
        {
            name: "timeout_sec",
            component: "number",
            fieldProps: {
                label: "Timeout (seconds)",
                defaultValue: 300,
                docs: {
                    text: "How long the MCP server waits for the tool execution before returning a timeout error with the execution id.",
                    urlPath: "/docs/ai-agents/mcp-server#user-tools",
                },
            },
            fieldAttr: enabledToolProps,
        },
        {
            name: "is_enabled",
            component: "switch",
            fieldProps: {
                label: "Enabled",
                defaultValue: false,
                docs: {
                    text: "When enabled, this tool appears in MCP tools/list and can be invoked by agents.",
                    urlPath: "/docs/ai-agents/mcp-server#user-tools",
                },
            },
            fieldAttr: isEnabledFieldProps,
        },
        {
            name: "announced",
            component: "switch",
            fieldProps: {
                label: "Announced",
                defaultValue: false,
                docs: {
                    text: "When on, the tool is listed in MCP tools/list. When off (the default for new tools), it is hidden from the list but stays fully callable and discoverable on demand via search_tools — keeping large tool catalogs from inflating an agent's context.",
                    urlPath: "/docs/ai-agents/mcp-server#announcing-user-tools",
                },
            },
        },
        {
            name: "read_only_hint",
            component: "switch",
            fieldProps: {
                label: "Read-only hint",
                defaultValue: false,
                docs: {
                    text: "Advertise readOnlyHint in MCP tools/list so clients may auto-approve the tool. Enable only when the flow does not modify workspace or external state.",
                    urlPath: "/docs/ai-agents/mcp-server#user-tools",
                },
            },
            fieldAttr: readOnlyHintFieldProps,
        },
        {
            name: "destructive_hint",
            component: "switch",
            fieldProps: {
                label: "Destructive hint",
                defaultValue: true,
                docs: {
                    text: "When read-only hint is off, advertise destructiveHint so clients may ask for confirmation before invoking the tool. Disable for flows that only create or update data without irreversible effects.",
                    urlPath: "/docs/ai-agents/mcp-server#user-tools",
                },
            },
            fieldAttr: destructiveHintFieldProps,
        },
        {
            name: "flow_schema_preview",
            component: "codeEditor",
            fieldProps: {
                label: "Flow schemas (read-only)",
                lang: "yaml",
                isReadOnly: true,
                // Read-only JSON⇄YAML display toggle for this structured value.
                structuredDataView: true,
                valueTransformer: (_value: unknown, allValues: { flow_id_flow?: { input_schema?: unknown; output_schema?: unknown } }) => {
                    const flow = allValues?.flow_id_flow;
                    if (!flow) {
                        return serializeToPrettyYaml({
                            note: "Select a flow and save to preview its input_schema and output_schema here.",
                        });
                    }
                    return serializeToPrettyYaml({
                        input_schema: flow.input_schema ?? {},
                        output_schema: flow.output_schema ?? {},
                    });
                },
                docs: {
                    text: "MCP inputSchema is taken from the flow input_schema. Edit schemas on the flow record.",
                    urlPath: "/docs/ai-agents/mcp-server#user-tools",
                },
            },
        },
        {
            name: "mcp_tool_preview",
            component: "codeEditor",
            fieldProps: {
                label: "MCP tool preview (read-only)",
                lang: "yaml",
                isReadOnly: true,
                // Read-only JSON⇄YAML display toggle for this structured value.
                structuredDataView: true,
                valueTransformer: (
                    _value: unknown,
                    allValues: {
                        name?: string;
                        description?: string;
                        read_only_hint?: boolean;
                        destructive_hint?: boolean;
                        flow_id_flow?: { input_schema?: unknown; output_schema?: unknown };
                    },
                ) => {
                    const title = (allValues?.name || "")
                        .replace(/-/g, " ")
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (char) => char.toUpperCase());
                    const outputSchema = mcpOutputSchemaFromFlow(allValues?.flow_id_flow?.output_schema);
                    return serializeToPrettyYaml({
                        name: allValues?.name,
                        title,
                        description: allValues?.description ?? "",
                        inputSchema: allValues?.flow_id_flow?.input_schema ?? {},
                        // Advertised only for an object output_schema (matches the backend gate);
                        // otherwise omitted, i.e. raw-text passthrough with no structuredContent.
                        ...(outputSchema ? { outputSchema } : {}),
                        annotations: buildMcpToolAnnotations(allValues),
                        naming_rules:
                            "The record name is the MCP tool name. It must be unique and cannot match a built-in MCP tool (for example run_flow).",
                    });
                },
            },
        },
    ],
    statsSidebar: [getLatestExecutions("mcp_user_tool")],
    formDTO: {
        initialValueDTO: (resourceData: Record<string, unknown>) => resourceData,
    },
};

export default config;
